import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  UserPlus,
  Mail,
  Lock,
  User,
  Shield,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
} from 'lucide-react'

import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'

const Register = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required')
      return false
    }
    if (!formData.email.trim()) {
      setError('Email is required')
      return false
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      setError('Please enter a valid email address')
      return false
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return false
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    if (!termsAccepted) {
      setError('You must accept the terms and conditions')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500))

      // Simulate successful registration
      setSuccess('Registration successful! Redirecting to login...')

      // Auto redirect after 3 seconds
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (err) {
      setError('Registration failed. Please try again.')
      console.error('Registration error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const passwordStrength = () => {
    const password = formData.password
    if (!password) return { strength: 'none', score: 0, color: 'text-secondary-400' }

    let score = 0
    if (password.length >= 8) score++
    if (/[A-Z]/.test(password)) score++
    if (/[a-z]/.test(password)) score++
    if (/[0-9]/.test(password)) score++
    if (/[^A-Za-z0-9]/.test(password)) score++

    if (score === 0) return { strength: 'none', score: 0, color: 'text-secondary-400' }
    if (score === 1) return { strength: 'Very Weak', score, color: 'text-error-600' }
    if (score === 2) return { strength: 'Weak', score, color: 'text-warning-600' }
    if (score === 3) return { strength: 'Good', score, color: 'text-primary-600' }
    if (score === 4) return { strength: 'Strong', score, color: 'text-success-600' }
    return { strength: 'Very Strong', score, color: 'text-success-700' }
  }

  const strength = passwordStrength()

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-secondary-900 dark:to-secondary-950 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Create Account
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-2">
            Join the V2Board manager platform
          </p>
        </div>

        <Card className="relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary-100 dark:bg-primary-900/20 rounded-full translate-x-20 -translate-y-16" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-secondary-100 dark:bg-secondary-800/20 rounded-full -translate-x-16 translate-y-16" />

          <div className="relative">
            {error && (
              <div className="mb-6 p-4 rounded-lg border border-error-200 bg-error-50 dark:border-error-800 dark:bg-error-900/20">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400" />
                  <p className="text-error-700 dark:text-error-300">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 rounded-lg border border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
                  <p className="text-success-700 dark:text-success-300">{success}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <Input
                  label="Full Name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  leftIcon={<User className="w-5 h-5" />}
                />

                <Input
                  label="Email Address"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  leftIcon={<Mail className="w-5 h-5" />}
                />

                <div>
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    leftIcon={<Lock className="w-5 h-5" />}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    }
                  />

                  {/* Password strength indicator */}
                  {formData.password && (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className={strength.color}>{strength.strength}</span>
                        <span className="text-secondary-500">{strength.score}/5</span>
                      </div>
                      <div className="h-1 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${
                            strength.score === 0 ? 'w-0' :
                            strength.score === 1 ? 'w-1/5 bg-error-500' :
                            strength.score === 2 ? 'w-2/5 bg-warning-500' :
                            strength.score === 3 ? 'w-3/5 bg-primary-500' :
                            strength.score === 4 ? 'w-4/5 bg-success-500' :
                            'w-full bg-success-600'
                          }`}
                        />
                      </div>
                      <ul className="grid grid-cols-2 gap-1 text-xs text-secondary-500 dark:text-secondary-400 mt-2">
                        <li className="flex items-center gap-1">
                          <CheckCircle className={`w-3 h-3 ${
                            formData.password.length >= 8 ? 'text-success-500' : 'text-secondary-400'
                          }`} />
                          <span>8+ characters</span>
                        </li>
                        <li className="flex items-center gap-1">
                          <CheckCircle className={`w-3 h-3 ${
                            /[A-Z]/.test(formData.password) ? 'text-success-500' : 'text-secondary-400'
                          }`} />
                          <span>Uppercase</span>
                        </li>
                        <li className="flex items-center gap-1">
                          <CheckCircle className={`w-3 h-3 ${
                            /[a-z]/.test(formData.password) ? 'text-success-500' : 'text-secondary-400'
                          }`} />
                          <span>Lowercase</span>
                        </li>
                        <li className="flex items-center gap-1">
                          <CheckCircle className={`w-3 h-3 ${
                            /[0-9]/.test(formData.password) ? 'text-success-500' : 'text-secondary-400'
                          }`} />
                          <span>Number</span>
                        </li>
                        <li className="flex items-center gap-1 col-span-2">
                          <CheckCircle className={`w-3 h-3 ${
                            /[^A-Za-z0-9]/.test(formData.password) ? 'text-success-500' : 'text-secondary-400'
                          }`} />
                          <span>Special character</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                <Input
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                  leftIcon={<Shield className="w-5 h-5" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  }
                />
              </div>

              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 mt-0.5"
                  />
                  <span className="text-sm text-secondary-600 dark:text-secondary-400">
                    I agree to the{' '}
                    <a
                      href="#"
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium"
                      onClick={(e) => {
                        e.preventDefault()
                        alert('Terms & Conditions - Coming soon!')
                      }}
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="#"
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium"
                      onClick={(e) => {
                        e.preventDefault()
                        alert('Privacy Policy - Coming soon!')
                      }}
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 mt-0.5"
                    defaultChecked
                  />
                  <span className="text-sm text-secondary-600 dark:text-secondary-400">
                    I want to receive updates about new features and improvements
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={isLoading}
                leftIcon={isLoading ? null : <UserPlus className="w-5 h-5" />}
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-secondary-600 dark:text-secondary-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </div>

            {/* Registration Benefits */}
            <div className="mt-8 pt-6 border-t border-secondary-200 dark:border-secondary-700">
              <h4 className="font-medium text-secondary-900 dark:text-white mb-3">
                With your account you get:
              </h4>
              <ul className="space-y-2 text-sm text-secondary-600 dark:text-secondary-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success-500" />
                  <span>Unlimited providers management</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success-500" />
                  <span>Real-time sync monitoring</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success-500" />
                  <span>Mobile-friendly dashboard</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success-500" />
                  <span>Advanced security features</span>
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default Register